// ------------------------------------------------------------------------------
// <copyright file="scenarioStorage.js" company="C2HL">
//     Copyright (c) 2024 C2HL. All rights reserved.
// </copyright>
// ------------------------------------------------------------------------------

/**
 * Scenario Testing — localStorage persistence for test credentials.
 *
 * Provides save/load/delete/clear operations for test tenant credentials
 * created during scenario runs. Entries auto-expire after 15 minutes
 * (matching the TTL=900 on seeded Cosmos documents).
 */

/** @type {string} localStorage key for scenario credentials */
export const STORAGE_KEY = 'meetingsync_scenario_credentials';

/** @type {number} Expiry duration in milliseconds (15 minutes) */
const EXPIRY_MS = 15 * 60 * 1000;

/**
 * Load all saved credential entries from localStorage.
 *
 * @returns {Array<object>} Array of credential entries, newest first.
 *   Returns empty array on parse error or missing key.
 */
export function loadCredentials() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const entries = JSON.parse(raw);
    // Newest first
    return [...entries].sort((a, b) => b.createdAt - a.createdAt);
  } catch {
    return [];
  }
}

/**
 * Save a credential entry to localStorage.
 *
 * Auto-generates a unique `id` and `createdAt` timestamp.
 *
 * @param {object} entry - The credential data to persist.
 * @param {string} entry.scenarioId - Scenario definition ID.
 * @param {string} entry.scenarioName - Human-readable scenario name.
 * @param {string} entry.email - Test user email.
 * @param {string} entry.password - Test user password.
 * @param {string} entry.tenantId - Extracted tenant ID.
 * @param {string} entry.planTier - Plan tier label.
 * @returns {object} The saved entry with generated `id` and `createdAt`.
 */
export function saveCredentials(entry) {
  const saved = {
    ...entry,
    id: crypto.randomUUID(),
    createdAt: Date.now(),
  };

  try {
    const existing = loadCredentials();
    // loadCredentials returns newest-first; restore chronological for storage
    const chronological = [...existing].sort((a, b) => a.createdAt - b.createdAt);
    chronological.push(saved);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(chronological));
  } catch {
    // Storage full or unavailable — best-effort
  }

  return saved;
}

/**
 * Delete a single credential entry by its ID.
 *
 * @param {string} id - The entry ID to remove.
 */
export function deleteCredential(id) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const entries = JSON.parse(raw);
    const filtered = entries.filter(e => e.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  } catch {
    // Best-effort
  }
}

/**
 * Remove all saved credential entries.
 */
export function clearAllCredentials() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([]));
  } catch {
    // Best-effort
  }
}

/**
 * Check whether a credential entry has expired (older than 15 minutes).
 *
 * @param {object} entry - A credential entry with a `createdAt` timestamp.
 * @param {number} entry.createdAt - Unix timestamp (ms) when entry was created.
 * @returns {boolean} True if the entry is expired.
 */
export function isExpired(entry) {
  return Date.now() - entry.createdAt > EXPIRY_MS;
}
